/**
 * Room Templates React Query Hooks
 * 
 * React Query hooks for room template data management
 * Date: 15-10-2025 16:00:00
 * 
 * @module hooks/api/useRoomTemplates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { roomTemplatesApi } from '../../api/endpoints/room-templates';
import type { 
  CreateRoomTemplateData, 
  UpdateRoomTemplateData, 
  RoomTemplateFilters 
} from '@shared/types/room-template';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const roomTemplateQueryKeys = {
  /** All templates query key */
  all: ['room-templates'] as const,
  
  /** Templates list query key */
  list: (filters?: RoomTemplateFilters) => ['room-templates', 'list', filters] as const,
  
  /** Single template query key */
  detail: (id: string) => ['room-templates', 'detail', id] as const,
  
  /** Templates by category query key */
  byCategory: (category: string) => ['room-templates', 'category', category] as const,
};

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch room templates with filtering
 * 
 * @param filters - Filter options
 * @returns React Query result with templates data
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useRoomTemplates({ 
 *   category: 'OPERATING_ROOM', 
 *   isActive: true 
 * });
 * ```
 */
export function useRoomTemplates(filters?: RoomTemplateFilters) {
  return useQuery({
    queryKey: roomTemplateQueryKeys.list(filters),
    queryFn: () => roomTemplatesApi.getTemplates(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch single room template by ID
 * 
 * @param templateId - Template ID to fetch
 * @returns React Query result with template data
 */
export function useRoomTemplate(templateId: string) {
  return useQuery({
    queryKey: roomTemplateQueryKeys.detail(templateId),
    queryFn: () => roomTemplatesApi.getTemplate(templateId),
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch templates by category
 * 
 * @param category - Template category
 * @returns React Query result with templates data
 */
export function useRoomTemplatesByCategory(category: string) {
  return useQuery({
    queryKey: roomTemplateQueryKeys.byCategory(category),
    queryFn: () => roomTemplatesApi.getTemplatesByCategory(category),
    enabled: !!category,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new room template
 * 
 * @returns Mutation for creating templates
 * 
 * @example
 * ```tsx
 * const createTemplateMutation = useCreateRoomTemplate();
 * 
 * const handleCreate = () => {
 *   createTemplateMutation.mutate({
 *     name: 'Pediatric ICU',
 *     category: 'PEDIATRIC',
 *     defaultWidth: 120,
 *     defaultHeight: 100,
 *     defaultCapacity: 2
 *   });
 * };
 * ```
 */
export function useCreateRoomTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateRoomTemplateData) => roomTemplatesApi.createTemplate(data),
    onSuccess: (response) => {
      // Invalidate templates list to refresh data
      queryClient.invalidateQueries({ queryKey: roomTemplateQueryKeys.all });
      
      notifications.show({
        title: 'Template Created',
        message: response.data?.template?.name ? `${response.data.template.name} created successfully` : 'Template created successfully',
        color: 'green'
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Creation Failed',
        message: error.response?.data?.error?.message || 'Failed to create template',
        color: 'red'
      });
    }
  });
}

/**
 * Hook to update room template
 * 
 * @returns Mutation for updating templates
 */
export function useUpdateRoomTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoomTemplateData }) => 
      roomTemplatesApi.updateTemplate(id, data),
    onSuccess: (response, variables) => {
      // Invalidate specific template and templates list
      queryClient.invalidateQueries({ queryKey: roomTemplateQueryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: roomTemplateQueryKeys.all });
      
      notifications.show({
        title: 'Template Updated',
        message: response.data?.template?.name ? `${response.data.template.name} updated successfully` : 'Template updated successfully',
        color: 'green'
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Update Failed',
        message: error.response?.data?.error?.message || 'Failed to update template',
        color: 'red'
      });
    }
  });
}

/**
 * Hook to delete room template
 * 
 * @returns Mutation for deleting templates
 */
export function useDeleteRoomTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => roomTemplatesApi.deleteTemplate(id),
    onSuccess: () => {
      // Invalidate templates list to refresh data
      queryClient.invalidateQueries({ queryKey: roomTemplateQueryKeys.all });
      
      notifications.show({
        title: 'Template Deleted',
        message: 'Template deleted successfully',
        color: 'green'
      });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || 'Failed to delete template';
      const isInUse = error.response?.data?.error?.code === 'TEMPLATE_IN_USE';
      
      notifications.show({
        title: 'Deletion Failed',
        message: isInUse 
          ? 'Cannot delete template that is being used by rooms'
          : errorMessage,
        color: 'red'
      });
    }
  });
}
